/**
 * SenderAvatar — small icon-or-initials avatar used in inbox rows,
 * document rows, chat thread headers, and any list surface that
 * scans by **who** or **what**.
 *
 * Responsibilities
 * ----------------
 *  - Render a square (rounded-md) chip with either INITIALS (default)
 *    OR a caller-provided ICON (Lucide glyph for file MIME types).
 *  - Apply a caller-supplied tone pair (``toneBg`` + ``toneFg``) so the
 *    consumer keeps the colour decision in its own domain layer (the
 *    inbox uses a deterministic per-address hash; documents use
 *    :func:`mimeTone` per file family; chat-thread headers might just
 *    pass the brand neutral). NO colour heuristic lives in this
 *    primitive.
 *  - Stay decorative (``aria-hidden``) so screen readers don't
 *    duplicate the sender name that always sits next to it.
 *
 * Modes
 * -----
 *  - ``icon`` provided → renders the icon (typically a 14px Lucide
 *    glyph). Used for FILE rows where the MIME family glyph is more
 *    recognisable than two letters of the filename.
 *  - otherwise → renders 2-letter initials derived from ``sender``
 *    (with ``initialsOverride`` taking precedence). Used for SENDER
 *    rows (inbox / chat).
 *
 * Tailwind classes are passed verbatim — DON'T interpolate them, or
 * the JIT compiler would purge them.
 */

import type * as React from "react";

import { cn } from "../lib/cn";


/** Two-letter initials from a sender display string. Empty → "??". */
function initialsFrom(sender: string | undefined | null): string {
  const trimmed = (sender ?? "").trim();
  if (!trimmed) return "??";
  // Strip an angle-bracketed email — keeps initials from the friendly
  // name when present (``Mario Rossi <m@x.it>`` → ``MR``).
  const friendly = trimmed.replace(/<[^>]+>/, "").trim() || trimmed;
  const parts = friendly.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const a = parts[0]?.[0] ?? "";
    const b = parts[1]?.[0] ?? "";
    return (a + b).toUpperCase();
  }
  return friendly.slice(0, 2).toUpperCase();
}


export interface SenderAvatarProps {
  /** Sender display string — used for initials AND as the title
   *  tooltip when ``title`` is omitted. Optional when :prop:`icon` is
   *  supplied. */
  readonly sender?: string | undefined | null;
  /** Tailwind background class — caller-resolved (e.g. ``bg-rose-50``). */
  readonly toneBg: string;
  /** Tailwind foreground class — caller-resolved (e.g. ``text-rose-700``). */
  readonly toneFg: string;
  /** Box size in px. Defaults to 28 (matches inbox + documents row spec). */
  readonly size?: number;
  /** Optional explicit initials override (already uppercased). */
  readonly initialsOverride?: string;
  /** Optional icon — when present, REPLACES initials. Use a Lucide
   *  glyph sized ~half the box (14px for the default 28px box). */
  readonly icon?: React.ReactNode;
  /** Optional native ``title=`` tooltip — defaults to the sender. */
  readonly title?: string;
  readonly className?: string;
}


export const SenderAvatar: React.FC<SenderAvatarProps> = ({
  sender,
  toneBg,
  toneFg,
  size = 28,
  initialsOverride,
  icon,
  title,
  className,
}) => {
  const content = icon ?? (initialsOverride ?? initialsFrom(sender));
  return (
    <span
      aria-hidden
      title={title ?? (sender ?? "")}
      style={{ width: size, height: size }}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md border border-white/70 font-semibold text-[10px]",
        toneBg,
        toneFg,
        className,
      )}
    >
      {content}
    </span>
  );
};
SenderAvatar.displayName = "SenderAvatar";
